import { safeStorage, createToggleRow } from '@learnforge/demo-kit';
import { Fraction } from '@learnforge/demo-math/numeric';
import { simplifyRatio } from '../arith/ratio';
import { createGrid, type GridHandle } from '../render/grid';
import { createSvgEl } from '@learnforge/demo-render';
import type { ArithContext, ArithKey } from '../types';
import { defineExperiment, type CustomExperimentSpec } from '@learnforge/demo-render/harness';

export interface GridShading {
  fullyShaded: Set<number>;
  partialShaded: Map<number, number>;
}

// Maps a fraction onto `totalCells` grid cells: whole cells fully shaded, the
// fractional remainder as one partial cell (e.g. 1/3 of 100 → 33 full + 0.333 partial).
export function gridShadingForFraction(fraction: Fraction, totalCells: number): GridShading {
  const exact = fraction.toNumber() * totalCells;
  const fullCount = Math.max(0, Math.min(totalCells, Math.floor(exact + 1e-9)));
  const fullyShaded = new Set<number>();
  for (let i = 0; i < fullCount; i += 1) fullyShaded.add(i);
  const partial = exact - fullCount;
  const partialShaded = new Map<number, number>();
  if (partial > 1e-6 && fullCount < totalCells) {
    partialShaded.set(fullCount, partial);
  }
  return { fullyShaded, partialShaded };
}

// Parses any of the three percent-equivalent forms — "3/4", "0.75", "75%" —
// into a Fraction. Returns null if the text can't be read.
export function parseAnyForm(input: string): Fraction | null {
  const t = input.trim();
  if (t === '') return null;
  try {
    if (t.endsWith('%')) {
      const n = Number(t.slice(0, -1).trim());
      if (!Number.isFinite(n)) return null;
      return new Fraction(Math.round(n * 100), 10000);
    }
    if (t.includes('/')) return Fraction.parse(t);
    const num = Number(t);
    if (!Number.isFinite(num)) return null;
    return Fraction.from(num);
  } catch {
    return null;
  }
}

function clamp01(f: Fraction): Fraction {
  if (f.toNumber() < 0) return Fraction.ZERO;
  if (f.toNumber() > 1) return Fraction.ONE;
  return f;
}

interface State {
  value: Fraction;
  advanced: boolean;
}

const TOTAL = 100;
const ADVANCED_STORAGE_KEY = 'arithmetic.percent-grid.advanced';

function buildSpec(): CustomExperimentSpec<State, ArithKey, ArithContext> {
  return {
    labelKey: 'experiment.percent-grid.label',
    demoId: 'arithmetic',
    advancedKey: 'percent-grid',

    initialState(ctx): State {
      return {
        value: new Fraction(47, 100),
        advanced: ctx.advanced,
      };
    },

    createRender(ctx, host, controls) {
      // ── DOM: SVG stage ──
      const svg = createSvgEl('svg', { viewBox: '0 0 480 512', class: 'arith-stage' });
      host.appendChild(svg);

      // The symbolic shaded : unshaded ratio line + caption sit above the grid;
      // the fraction/decimal/percent readout stays below it.
      const ratioGroup = createSvgEl('g', {});
      svg.appendChild(ratioGroup);

      const gridGroup = createSvgEl('g', { transform: 'translate(40, 74)' });
      svg.appendChild(gridGroup);

      const readout = createSvgEl('text', {
        class: 'arith-readout',
        x: 240,
        y: 498,
        'text-anchor': 'middle',
      });
      svg.appendChild(readout);

      // Transient closure vars — NOT in State
      let gridHandle: GridHandle | null = null;
      let inputEl: HTMLInputElement | null = null;

      // ── Panel: built ONCE; update() reconciles visibility ──
      const advInputWrap = document.createElement('div');
      advInputWrap.className = 'arith-adv-controls';
      const advLabel = document.createElement('label');
      advLabel.htmlFor = 'percent-grid-value';
      advLabel.textContent = ctx.i18n.t('panel.enter-number');
      const input = document.createElement('input');
      input.id = 'percent-grid-value';
      input.type = 'text';
      input.placeholder = '3/4, 0.75, 75%';
      input.addEventListener('change', () => {
        const parsed = parseAnyForm(input.value);
        if (parsed === null) {
          input.classList.add('invalid');
          return;
        }
        input.classList.remove('invalid');
        controls.setState((s) => ({ ...s, value: clamp01(parsed) }));
      });
      inputEl = input;
      advInputWrap.appendChild(advLabel);
      advInputWrap.appendChild(input);

      const advToggle = createToggleRow({
        i18n: ctx.i18n,
        labelKey: 'btn.advanced',
        checked: ctx.advanced,
        onChange(next) {
          safeStorage.set(ADVANCED_STORAGE_KEY, next ? 'true' : 'false');
          controls.setState((s) => ({ ...s, advanced: next }));
        },
      });

      ctx.panel.appendChild(advInputWrap);
      ctx.panel.appendChild(advToggle.element);

      // ── SVG helpers ──
      function swatch(x: number, shaded: boolean): SVGRectElement {
        return createSvgEl('rect', {
          class: shaded ? 'arith-swatch shaded' : 'arith-swatch',
          x,
          y: 24,
          width: 14,
          height: 14,
          rx: 2,
        });
      }

      function ratioNumber(x: number, value: number): SVGTextElement {
        const t = createSvgEl('text', {
          class: 'arith-readout',
          x,
          y: 31,
          'dominant-baseline': 'central',
        });
        t.textContent = String(value);
        return t;
      }

      function renderRatioLine(shadedCount: number): void {
        while (ratioGroup.firstChild) ratioGroup.removeChild(ratioGroup.firstChild);
        const unshaded = TOTAL - shadedCount;
        const simplified = simplifyRatio([shadedCount, unshaded]);
        const rn = simplified[0] ?? 0;
        const rd = simplified[1] ?? 0;

        ratioGroup.appendChild(swatch(184, true));
        ratioGroup.appendChild(ratioNumber(203, shadedCount));
        const colon = createSvgEl('text', {
          class: 'arith-readout',
          x: 240,
          y: 31,
          'text-anchor': 'middle',
          'dominant-baseline': 'central',
        });
        colon.textContent = ':';
        ratioGroup.appendChild(colon);
        ratioGroup.appendChild(swatch(260, false));
        ratioGroup.appendChild(ratioNumber(279, unshaded));

        const caption = createSvgEl('text', {
          class: 'arith-ratio-caption',
          x: 240,
          y: 56,
          'text-anchor': 'middle',
        });
        caption.textContent = `shaded : unshaded  =  ${rn} : ${rd}`;
        ratioGroup.appendChild(caption);
      }

      // ── RenderHandle ──
      function update(state: State): void {
        const { value, advanced } = state;
        const shadedCount = Math.round(value.toNumber() * TOTAL);

        // Reconcile grid (destroy + recreate on every state change, as original)
        gridHandle?.destroy();
        const { fullyShaded, partialShaded } = gridShadingForFraction(value, TOTAL);
        gridHandle = createGrid(gridGroup, {
          cols: 10,
          rows: 10,
          shaded: fullyShaded,
          partialShaded,
          width: 400,
          height: 400,
          onCellClick(index) {
            // Fill up to (and including) the clicked cell.
            controls.setState((s) => ({ ...s, value: new Fraction(index + 1, TOTAL) }));
          },
        });

        // Reconcile readout
        readout.textContent = `${value.toString()}  =  ${value.toDecimalString(2)}  =  ${value.toPercentString()}`;
        renderRatioLine(shadedCount);

        // Guarded input value sync — only update when not actively editing
        if (inputEl !== null && inputEl !== document.activeElement) {
          const display = value.toString();
          if (inputEl.value !== display) inputEl.value = display;
        }

        // Show/hide advanced input wrap
        advInputWrap.style.display = advanced ? '' : 'none';
      }

      return {
        update,
        dispose() {
          gridHandle?.destroy();
          gridHandle = null;
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());
